import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { motion } from "framer-motion";
import { database } from "../../../../appWrite";
import { fetchTestimonials } from "../../../../redux/slices/TestimonialSlice";
import { useAppDispatch } from "../../../../redux/hooks";

interface Testimonial {
  name: string;
  heading: string;
  review: string;
  occupation: string;
  image?: FileList;
}

interface TestimonialEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  testimonial: any; // The testimonial to edit
}

const TestimonialEditModal: React.FC<TestimonialEditModalProps> = ({
  isOpen,
  onClose,
  testimonial,
}) => {
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Testimonial>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (testimonial) {
      reset({
        name: testimonial.name,
        heading: testimonial.heading,
        review: testimonial.review,
        occupation: testimonial.occupation,
      });
    }
  }, [testimonial, reset]);

  const onSubmit: SubmitHandler<Testimonial> = async (data) => {
    setLoading(true);
    try {
      // Update Testimonial
      await database.updateDocument(
        "67ce8a4a0032e13c6368",
        "67d7eb5a0013791e9da4",
        testimonial.$id,
        {
          name: data.name,
          heading: data.heading,
          review: data.review,
          occupation: data.occupation,
        }
      );

      dispatch(fetchTestimonials());
      onClose();
    } catch (error) {
      console.error("Error updating testimonial:", error);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const input =
    "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary";
  const btnPrimary =
    "px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90";
  const btnSecondary =
    "px-4 py-2 bg-gray-200 text-black rounded-md hover:bg-gray-300";

  return (
    <motion.div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-3 md:mx-0"
      >
        <h2 className="text-xl font-bold mb-4">Edit Testimonial</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input
            {...register("name", { required: true })}
            placeholder="Name"
            className={input}
          />
          {errors.name && (
            <p className="text-red-500 text-sm">Name is required</p>
          )}

          <input
            {...register("occupation", { required: true })}
            placeholder="Occupation"
            className={input}
          />
          {errors.occupation && (
            <p className="text-red-500 text-sm">Occupation is required</p>
          )}

          <input
            {...register("heading", { required: true })}
            placeholder="Heading"
            className={input}
          />
          {errors.heading && (
            <p className="text-red-500 text-sm">Heading is required</p>
          )}

          <textarea
            {...register("review", { required: true })}
            placeholder="Review"
            className={input}
          ></textarea>
          {errors.review && (
            <p className="text-red-500 text-sm">Review is required</p>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={btnSecondary}>
              Cancel
            </button>
            <button type="submit" className={btnPrimary} disabled={loading}>
              {loading ? "Saving..." : "Update"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default TestimonialEditModal;
